import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ApplicationState } from '../../store';
import { ICustomer } from '../../store/ducks/customers/types';
import {
  loadCustomerBlocks,
  loadCustomersProductDescriptionV2
} from '../../store/ducks/customers/actions';
import {
  loadReceiversForBP,
  loadReceiversFrom
} from '../../store/ducks/entry-order/actions';
import { updateLongSteelShoppingCartSettings } from '../../store/ducks/long-steel-shopping-cart/actions';
import { removeSearchedItem } from '../../store/ducks/long-steel-order-item-search/actions';
import { updateCartItemsQuantity } from '../../store/ducks/cart-items-mvp/actions';
import { EOrderSettingsRoutes } from '../../pages/Purchase/LongSteel/OrderSettingsMvp/orderSettings.routes';
import { GetFlag } from '../../utils/featureFlag';
import { useProductForCustomer } from './useProductForCustomer';
import { useCartService } from './useCartService';

export const useSelectEmitter = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const newEmitterReceiverSelect = GetFlag('new_emitter_receiver_select');
  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');

  const { customerProductIndex } = useProductForCustomer();
  const { getCartItemQuantity } = useCartService();

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const userState = useSelector((state: ApplicationState) => state.user);

  const purchaseCustomers = useSelector((state: ApplicationState) =>
    isUserAdmin
      ? state.customers.customers
      : state.customers.customers.filter((customer) =>
          userState.roles.ordersInputCustomers.includes(customer.customerId)
        )
  );

  const getCustomer = (customerId: string): ICustomer | undefined =>
    purchaseCustomers.find((customer) => customer.customerId === customerId);

  const loadCustomerBlocksIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.blocks &&
      !customersState.loadingBlocks &&
      !customersState.errorBlocks
    ) {
      dispatch(loadCustomerBlocks(customer.customerId));
    }
  };

  const loadProductDescriptionIfNeed = (customer: ICustomer) => {
    const isCatalogPage = pathname.includes(EOrderSettingsRoutes.CATALOG);
    const isSearchPage = pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS);
    const descriptionResponse =
      newSearchItemsEndpointFlag || customer?.productsDescriptionResponse;
    if (
      customer &&
      (isCatalogPage || isSearchPage) &&
      !descriptionResponse &&
      !customersState.loadingProductDescription &&
      !customersState.errorProductDescription &&
      !newSearchItemsEndpointFlag
    ) {
      dispatch(loadCustomersProductDescriptionV2(customer.customerId));
    }
  };

  const loadReceivers = (customer?: ICustomer) => {
    if (!customer) {
      return;
    }
    if (newEmitterReceiverSelect) {
      dispatch(loadReceiversForBP(customer.customerId));
    } else {
      dispatch(loadReceiversFrom(customer));
    }
  };

  const selectEmitter = async (
    emitterId: string,
    receiverId?: string,
    customerOrder?: string
  ): Promise<void> => {
    const finalShip = receiverId || emitterId;
    const finalCustomerOrder = customerOrder ?? shoppingCartState.customerOrder;

    if (
      emitterId !== shoppingCartState.emitterId ||
      finalShip !== shoppingCartState.receiverId ||
      finalCustomerOrder !== shoppingCartState.customerOrder
    ) {
      dispatch(
        updateLongSteelShoppingCartSettings({
          ...shoppingCartState,
          emitterId,
          receiverId: finalShip || emitterId,
          customerOrder: finalCustomerOrder
        })
      );
    }

    customerProductIndex(emitterId);

    const customer = getCustomer(emitterId);

    if (emitterId !== '' && customer) {
      loadProductDescriptionIfNeed(customer);
      loadCustomerBlocksIfNeed(customer);
    }

    loadReceivers(customer);

    dispatch(removeSearchedItem([]));

    const cartQuantity = await getCartItemQuantity({
      countType: true,
      soldTo: emitterId,
      shipTo: finalShip
    });
    dispatch(updateCartItemsQuantity(cartQuantity));
  };

  return { selectEmitter };
};

export default useSelectEmitter;
